import Link from 'next/link'
import type { TicketedEventPublic } from '@school-journey/shared-types'
import { ctaLabel, formatEventDate, statusPill, type PillTone } from '../_lib/event-presentation'

// Eventbrite-style pill palette. The hexes are the locked mockup tokens for
// light mode; each tone carries a dark-mode pair because these cards render
// inside the dashboard's theme provider, where a tinted-light chip with dark
// ink lands on a dark card and disappears.
const PILL_CLASS: Record<PillTone, string> = {
  pink: 'bg-[#fdf0f2] text-[#b3093c] dark:bg-[#b3093c]/20 dark:text-[#ff7a9b]',
  lav: 'bg-[#ebe9fb] text-[#3d2c8d] dark:bg-[#3d2c8d]/30 dark:text-[#bcb2f5]',
  green: 'bg-[#e6f4ee] text-[#14794a] dark:bg-[#14794a]/25 dark:text-[#6fd3a4]'
}

/**
 * One event tile, shared by the school hub grid and the collection page.
 *
 * Deliberately hook-free so a server component can render it — the collection
 * page has no filters and therefore no client state at all.
 */
function EventCard({
  schoolSlug,
  event,
  schoolTimezone
}: {
  schoolSlug: string
  event: TicketedEventPublic
  schoolTimezone: string | null | undefined
}) {
  const pill = statusPill(event)
  return (
    <Link
      href={`/events/${encodeURIComponent(schoolSlug)}/${encodeURIComponent(event.id)}`}
      className="group block"
      data-testid="event-card"
    >
      <div
        className="bg-muted relative overflow-hidden rounded-lg shadow-[0_1px_2px_rgba(40,44,53,.06)]"
        style={{ aspectRatio: '16 / 9' }}
      >
        {event.coverImageUrl ? (
          <img
            src={event.coverImageUrl}
            alt={event.title}
            className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
          />
        ) : (
          <div
            className="h-full w-full bg-linear-to-br from-[#ebe9fb] to-[#dbdae3] dark:from-[#3d2c8d] dark:to-[#1e0a3c]"
            data-testid="cover-fallback"
            aria-hidden="true"
          />
        )}
      </div>
      <div className="px-[2px] pt-3">
        {pill && (
          <span className={`mb-2 inline-block rounded-[5px] px-[9px] py-1 text-xs font-bold ${PILL_CLASS[pill.tone]}`}>
            {pill.label}
          </span>
        )}
        <h3 className="group-hover:text-primary mb-[6px] line-clamp-2 text-[17px] leading-tight font-bold tracking-[-.2px]">
          {event.title}
        </h3>
        <div className="text-foreground/85 mb-[3px] text-sm font-bold">
          {formatEventDate(event.startsAt, undefined, schoolTimezone)}
        </div>
        {event.locationText && (
          <div className="text-muted-foreground mb-[7px] truncate text-sm">{event.locationText}</div>
        )}
        <div className="text-foreground/85 text-sm font-bold">{ctaLabel({ kind: event.kind })}</div>
      </div>
    </Link>
  )
}

export { EventCard }
